/**
 * Reading a spritesheet export (spec 034): pure, no DOM, no zip library.
 * The Editor unpacks the file (jszip) and hands this module entry lists,
 * parsed JSON and PNG headers; what comes back is a list of animations,
 * each with the `SheetCut` it should get (spec 033).
 *
 * Formats read (D-2, D-7, D-8): a sheet PNG with its TexturePacker JSON
 * (hash or array form), a sheet PNG whose file name gives its grid, and a
 * folder of numbered frame PNGs. Anything that cannot be read is refused
 * with a `SheetImportError` whose message is shown to the author as is.
 */

import type { SheetCut, SheetRect } from '../@types';
import { SHEET_FPS_MAX, SHEET_FPS_MIN, SHEET_MAX_CELLS } from '../utils/spriteSheet';

/** A reason the file cannot be imported, worded for the author. */
export class SheetImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SheetImportError';
  }
}

/** D-3: the frame rate of a sheet without timing. */
export const IMPORT_DEFAULT_FPS = 12;
/** Limits checked before any image is decoded. */
export const IMPORT_MAX_ENTRIES = 400;
export const IMPORT_MAX_BYTES = 200 * 1024 * 1024;
export const IMPORT_MAX_SIDE = 8192;
/** A folder needs this many numbered PNGs to be read as frames. */
export const IMPORT_MIN_FRAMES = 3;

export interface GridFileName {
  /** The animation name (D-4): what comes before the pattern, normalised. */
  name: string;
  frameCount: number;
  rows: number;
  columns: number;
}

export interface ParsedSheetJson {
  /** The frames' rects, in frame order. */
  frames: SheetRect[];
  /** Per-frame ms, present only when every frame has a duration above 0. */
  frameMs?: number[];
  /** `meta.size`, when the file gives one: must match the decoded sheet. */
  metaSize?: { w: number; h: number };
  /** `meta.image`, when the file names its sheet. */
  metaImage?: string;
}

/** One zip entry as `classifyExport` needs it. */
export interface ExportEntry {
  /** Path inside the zip, `/`-separated. */
  path: string;
  /** Uncompressed bytes. */
  size: number;
  /** For a `.json` entry, its parsed content when the caller read it (pairs by `meta.image`). */
  json?: unknown;
}

export type ExportAnimation =
  | { kind: 'json'; name: string; sheetPath: string; jsonPath: string; grid?: GridFileName }
  | { kind: 'grid'; name: string; sheetPath: string; grid: GridFileName }
  | { kind: 'frames'; name: string; framePaths: string[] };

export interface ExportContents {
  /** What can be imported, in path order. */
  animations: ExportAnimation[];
  /** PNGs that are none of the formats: listed greyed, cut by hand (035). */
  unknown: { name: string; path: string }[];
  /** One animation is a single export; several are a pack. */
  pack: boolean;
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

const baseName = (path: string) => path.slice(path.lastIndexOf('/') + 1);
const dirName = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');
const stripExt = (name: string) => name.replace(/\.[a-z0-9]+$/i, '');
const extOf = (name: string) => (/\.([a-z0-9]+)$/i.exec(name)?.[1] ?? '').toLowerCase();

/** D-4: lower case, separators turned into spaces, trimmed. */
export function animationName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_\-.]+/g, ' ')
    .trim();
}

const LUDO_GRID = /^(.*?)-(\d+)px-frames-(\d+)-rows-(\d+)-cols-(\d+)$/i;
const SUFFIX_GRID = /^(.*?)[-_](\d+)x(\d+)$/i;

/**
 * D-8: the grid a file name gives, `-<size>px-frames-<n>-rows-<r>-cols-<c>`
 * or `_<c>x<r>` / `-<c>x<r>` before the extension, case-insensitive; null
 * when it has neither (or the numbers do not make a cut).
 */
export function parseGridFileName(fileName: string): GridFileName | null {
  const stem = stripExt(baseName(fileName));
  let raw: string;
  let frameCount: number;
  let rows: number;
  let columns: number;
  const ludo = LUDO_GRID.exec(stem);
  const suffix = ludo ? null : SUFFIX_GRID.exec(stem);
  if (ludo) {
    raw = ludo[1];
    frameCount = Number(ludo[3]);
    rows = Number(ludo[4]);
    columns = Number(ludo[5]);
  } else if (suffix) {
    raw = suffix[1];
    columns = Number(suffix[2]);
    rows = Number(suffix[3]);
    frameCount = columns * rows;
  } else {
    return null;
  }
  if (columns < 1 || rows < 1 || columns > SHEET_MAX_CELLS || rows > SHEET_MAX_CELLS) return null;
  if (frameCount < 1 || frameCount > columns * rows) return null;
  return { name: animationName(raw), frameCount, rows, columns };
}

/** The trailing number of a frame or file name (before any extension), or null. */
export function trailingNumber(name: string): number | null {
  const m = /(\d+)$/.exec(stripExt(baseName(name)));
  return m ? Number(m[1]) : null;
}

/** The name with its trailing number (and separator) removed. */
const withoutTrailingNumber = (name: string) => stripExt(baseName(name)).replace(/[\s_\-.]*\d+$/, '');

// ---------------------------------------------------------------------------
// TexturePacker JSON
// ---------------------------------------------------------------------------

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * D-7: either TexturePacker form, frames ordered by the trailing number of
 * their names (ties and unnumbered names keep the file's order). Refuses a
 * rotated or trimmed frame, a frame without a rect, and a file with no frames.
 */
export function parseSheetJson(json: unknown): ParsedSheetJson {
  if (!isObject(json) || !('frames' in json)) throw new SheetImportError('This JSON is not a TexturePacker frame file (it has no "frames")');
  const raw = json.frames;
  let list: { name: string; data: unknown }[];
  if (Array.isArray(raw)) {
    list = raw.map((f, i) => ({ name: isObject(f) && typeof f.filename === 'string' ? f.filename : String(i), data: f }));
  } else if (isObject(raw)) {
    list = Object.entries(raw).map(([name, data]) => ({ name, data }));
  } else {
    throw new SheetImportError('This JSON is not a TexturePacker frame file ("frames" is neither a list nor a map)');
  }
  if (list.length === 0) throw new SheetImportError('The JSON has no frames');

  const ordered = list
    .map((f, i) => ({ ...f, i, n: trailingNumber(f.name) }))
    .sort((a, b) => (a.n !== null && b.n !== null && a.n !== b.n ? a.n - b.n : a.i - b.i));

  const frames: SheetRect[] = [];
  const ms: number[] = [];
  let allTimed = true;
  for (const f of ordered) {
    const d = f.data;
    if (!isObject(d)) throw new SheetImportError(`Frame "${f.name}" has no rect`);
    if (d.rotated === true || d.trimmed === true) throw new SheetImportError('Export again without rotation or trimming');
    const r = d.frame;
    if (!isObject(r) || !isNum(r.x) || !isNum(r.y) || !isNum(r.w) || !isNum(r.h) || r.x < 0 || r.y < 0 || r.w <= 0 || r.h <= 0) {
      throw new SheetImportError(`Frame "${f.name}" has no valid rect`);
    }
    frames.push({ x: r.x, y: r.y, w: r.w, h: r.h });
    if (isNum(d.duration) && d.duration > 0) ms.push(d.duration);
    else allTimed = false;
  }

  const out: ParsedSheetJson = { frames };
  if (allTimed) out.frameMs = ms;
  const meta = isObject(json.meta) ? json.meta : undefined;
  if (meta && isObject(meta.size) && isNum(meta.size.w) && isNum(meta.size.h)) out.metaSize = { w: meta.size.w, h: meta.size.h };
  if (meta && typeof meta.image === 'string' && meta.image) out.metaImage = meta.image;
  return out;
}

/** `round(1000 × frames ÷ total ms)`, clamped to 1–60; 12 without timing (D-3). */
export function fpsFromDurations(frameMs: number[] | undefined): number {
  if (!frameMs || frameMs.length === 0) return IMPORT_DEFAULT_FPS;
  const total = frameMs.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return IMPORT_DEFAULT_FPS;
  return Math.min(SHEET_FPS_MAX, Math.max(SHEET_FPS_MIN, Math.round((1000 * frameMs.length) / total)));
}

/**
 * The cut a sheet + JSON gets: columns and rows from the file name's grid
 * when it has one (and it holds every frame), else from the distinct `x` and
 * `y` of the rects (widened so columns × rows holds every frame).
 */
export function cutFromJson(parsed: ParsedSheetJson, fileName?: string): SheetCut {
  const n = parsed.frames.length;
  const grid = fileName ? parseGridFileName(fileName) : null;
  let columns: number;
  let rows: number;
  if (grid && grid.columns * grid.rows >= n) {
    columns = grid.columns;
    rows = grid.rows;
  } else {
    columns = new Set(parsed.frames.map((r) => r.x)).size;
    rows = new Set(parsed.frames.map((r) => r.y)).size;
    if (columns * rows < n) rows = Math.ceil(n / columns);
  }
  if (columns > SHEET_MAX_CELLS) {
    columns = SHEET_MAX_CELLS;
    rows = Math.ceil(n / columns);
  }
  if (rows > SHEET_MAX_CELLS) throw new SheetImportError(`The sheet has ${n} frames; at most ${SHEET_MAX_CELLS * SHEET_MAX_CELLS} are read`);
  const json: NonNullable<SheetCut['json']> = { frames: parsed.frames };
  if (parsed.frameMs) json.frameMs = parsed.frameMs;
  return { cutBy: 'json', json, frameCount: n, fps: fpsFromDurations(parsed.frameMs), columns, rows, source: 'json' };
}

/** The cut a grid-named sheet gets (D-3: 12 fps). */
export function cutFromGrid(grid: GridFileName): SheetCut {
  return { cutBy: 'grid', frameCount: grid.frameCount, rows: grid.rows, columns: grid.columns, fps: IMPORT_DEFAULT_FPS, source: 'grid' };
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

/** The entry-count and total-size limits (the Editor counts unpacked bytes itself, `unpackZipEntries`). */
export function checkExportLimits(entries: readonly Pick<ExportEntry, 'size'>[]): void {
  if (entries.length > IMPORT_MAX_ENTRIES) {
    throw new SheetImportError(`The file holds ${entries.length} files; the limit is ${IMPORT_MAX_ENTRIES}`);
  }
  const total = entries.reduce((sum, e) => sum + (isNum(e.size) && e.size > 0 ? e.size : 0), 0);
  if (total > IMPORT_MAX_BYTES) {
    throw new SheetImportError(`The file unpacks to ${mb(total)}; the limit is ${mb(IMPORT_MAX_BYTES)}`);
  }
}

/** A decoded sheet's size against the side limit. */
export function checkSheetSize(size: { width: number; height: number }, name = 'A sheet'): void {
  const side = Math.max(size.width, size.height);
  if (side > IMPORT_MAX_SIDE) {
    throw new SheetImportError(`${name} is ${side} px on a side; the limit is ${IMPORT_MAX_SIDE} px`);
  }
}

/** `meta.size` against the decoded sheet (D-7). */
export function checkMetaSize(parsed: ParsedSheetJson, size: { width: number; height: number }, name = 'The sheet'): void {
  if (parsed.metaSize && (parsed.metaSize.w !== size.width || parsed.metaSize.h !== size.height)) {
    throw new SheetImportError(
      `${name} is ${size.width} × ${size.height} but its JSON says ${parsed.metaSize.w} × ${parsed.metaSize.h}; export them again together`,
    );
  }
}

/**
 * A PNG's width and height from its IHDR header (bytes 16–23), without
 * decoding it; null when the bytes are not a PNG.
 */
export function pngSize(bytes: Uint8Array): { width: number; height: number } | null {
  const SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length < 24 || SIG.some((b, i) => bytes[i] !== b)) return null;
  const u32 = (o: number) => ((bytes[o] << 24) | (bytes[o + 1] << 16) | (bytes[o + 2] << 8) | bytes[o + 3]) >>> 0;
  return { width: u32(16), height: u32(20) };
}

// ---------------------------------------------------------------------------
// Classifying a zip's entries
// ---------------------------------------------------------------------------

const ignored = (path: string) =>
  path.startsWith('__MACOSX/') || path.split('/').some((part) => part.startsWith('.')) || path.endsWith('/');

/**
 * D-2: a zip's entries as animations. Ignores `__MACOSX/`, dot files and
 * anything but `.png` and `.json`; pairs a PNG with the JSON of the same base
 * name in its folder (or whose `meta.image` names it); reads three or more
 * PNGs in one folder that differ only by a trailing number as frames; reads
 * any other PNG by its file name's grid, else lists it as unknown. Names
 * follow D-4. Refuses an export over the entry or size limits.
 */
export function classifyExport(entries: readonly ExportEntry[]): ExportContents {
  checkExportLimits(entries);
  const files = entries.filter((e) => !ignored(e.path) && (extOf(e.path) === 'png' || extOf(e.path) === 'json'));
  const pngs = files.filter((e) => extOf(e.path) === 'png').map((e) => e.path).sort();
  const jsons = files.filter((e) => extOf(e.path) === 'json');
  const used = new Set<string>();

  type Found = { folder: string; fileName: string; animation: Omit<ExportAnimation, 'name'> & { kind: ExportAnimation['kind'] } };
  const found: Found[] = [];

  // Sheet + JSON
  for (const json of jsons) {
    const folder = dirName(json.path);
    const stem = stripExt(baseName(json.path));
    let sheet = pngs.find((p) => !used.has(p) && dirName(p) === folder && stripExt(baseName(p)) === stem);
    if (!sheet && isObject(json.json) && isObject(json.json.meta) && typeof json.json.meta.image === 'string') {
      const image = json.json.meta.image;
      const target = folder ? `${folder}/${image}` : image;
      sheet = pngs.find((p) => !used.has(p) && (p === target || p === image));
    }
    if (!sheet) continue;
    used.add(sheet);
    const grid = parseGridFileName(sheet) ?? undefined;
    found.push({ folder: dirName(sheet), fileName: sheet, animation: { kind: 'json', sheetPath: sheet, jsonPath: json.path, ...(grid ? { grid } : {}) } as Found['animation'] });
  }

  // Numbered frames: per folder, PNGs whose names differ only by a trailing number
  const groups = new Map<string, string[]>();
  for (const p of pngs) {
    if (used.has(p) || trailingNumber(p) === null || parseGridFileName(p)) continue;
    const key = `${dirName(p)}\u0000${withoutTrailingNumber(p).toLowerCase()}`;
    groups.set(key, [...(groups.get(key) ?? []), p]);
  }
  for (const [key, paths] of groups) {
    if (paths.length < IMPORT_MIN_FRAMES) continue;
    const framePaths = [...paths].sort((a, b) => (trailingNumber(a) ?? 0) - (trailingNumber(b) ?? 0) || a.localeCompare(b));
    framePaths.forEach((p) => used.add(p));
    found.push({ folder: key.split('\u0000')[0], fileName: framePaths[0], animation: { kind: 'frames', framePaths } as Found['animation'] });
  }

  // Grid-named sheets, then the rest
  const unknown: ExportContents['unknown'] = [];
  for (const p of pngs) {
    if (used.has(p)) continue;
    const grid = parseGridFileName(p);
    if (grid) found.push({ folder: dirName(p), fileName: p, animation: { kind: 'grid', sheetPath: p, grid } as Found['animation'] });
    else unknown.push({ name: animationName(stripExt(baseName(p))) || 'sheet', path: p });
  }

  const pack = found.length > 1;
  const perFolder = new Map<string, number>();
  for (const f of found) perFolder.set(f.folder, (perFolder.get(f.folder) ?? 0) + 1);
  const folderName = (folder: string) => animationName(baseName(folder));

  const animations = found
    .map((f): ExportAnimation => {
      const ownFolder = f.folder !== '' && perFolder.get(f.folder) === 1;
      let name: string;
      if (f.animation.kind === 'frames') {
        name = f.folder ? folderName(f.folder) : animationName(withoutTrailingNumber(f.fileName));
      } else if (pack && ownFolder) {
        name = folderName(f.folder);
      } else {
        const grid = parseGridFileName(f.fileName);
        name = grid ? grid.name : animationName(stripExt(baseName(f.fileName)));
      }
      return { ...f.animation, name: name || 'animation' } as ExportAnimation;
    })
    .sort((a, b) => firstPath(a).localeCompare(firstPath(b)));

  return { animations, unknown, pack };
}

const firstPath = (a: ExportAnimation) => (a.kind === 'frames' ? a.framePaths[0] : a.sheetPath);
